import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useToast } from '../../hooks/use-toast';
import { 
  ArrowLeft, Building2, User, Mail, Phone, MapPin, Package, 
  Truck, Calendar, CheckCircle, ShoppingCart, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrivanaEnquiry = () => {
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    // Business Info
    businessName: '',
    businessType: '',
    contactPerson: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    gstNumber: '',
    
    // Product Requirements
    productCategory: searchParams.get('category') || '',
    productVariety: searchParams.get('product') || '',
    quantity: '',
    quantityUnit: 'kg',
    deliveryFrequency: '',
    preferredDeliveryDate: '',
    
    // Additional
    qualityGrade: '',
    packagingPreference: '',
    additionalRequirements: ''
  });

  const businessTypes = [
    'Wholesaler',
    'Retailer',
    'Restaurant/HORECA',
    'Supermarket Chain',
    'Export House',
    'Food Processor',
    'Institutional Buyer',
    'Other'
  ];

  const productCategories = [
    { value: 'grapes', label: 'Grapes' },
    { value: 'mangoes', label: 'Mangoes' },
    { value: 'pomegranates', label: 'Pomegranates' },
    { value: 'citrus', label: 'Citrus Fruits' },
    { value: 'onions', label: 'Onions' },
    { value: 'vegetables', label: 'Vegetables' },
    { value: 'mixed', label: 'Mixed Order' }
  ];

  const deliveryFrequencies = [
    'One-time Order',
    'Weekly',
    'Bi-weekly',
    'Monthly',
    'As per requirement'
  ];

  const qualityGrades = [
    'Export Grade A',
    'Grade A (Premium)',
    'Grade B (Standard)',
    'Processing Grade',
    'Any available'
  ];

  const packagingOptions = [
    'Standard Boxes (5kg)',
    'Standard Boxes (10kg)',
    'Bulk Crates (20kg)',
    'Custom Packaging',
    'As per your specification'
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/orivana/enquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          submittedAt: new Date().toISOString(),
          source: 'orivana-fresh'
        })
      });

      if (!response.ok) throw new Error('Failed to submit enquiry');

      setSubmitted(true);
      toast({
        title: "Enquiry Submitted!",
        description: "Our team will contact you within 24 hours.",
      });
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Submission Failed",
        description: "Please try again or contact us directly.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 py-20">
        <div className="container mx-auto px-4">
          <Card className="max-w-lg mx-auto text-center p-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#263776] mb-4">Thank You!</h2>
            <p className="text-slate-600 mb-6">
              Your enquiry has been submitted successfully. Our sales team will contact you within 24 hours with a customized quote.
            </p>
            <div className="bg-slate-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-slate-500">Reference Number</p>
              <p className="font-bold text-[#263776]">ORV-{Date.now().toString().slice(-8)}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild className="bg-[#263776] hover:bg-[#1e2d5f]">
                <Link to="/products/orivana-fresh">Continue Browsing</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/">Back to Home</Link>
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <section className="bg-[#263776] py-12">
        <div className="container mx-auto px-4">
          <Link to="/products/orivana-fresh" className="inline-flex items-center text-white/80 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to ORIVANA Fresh
          </Link>
          <Badge className="bg-[#F47D20] text-white mb-4 block w-fit">B2B Enquiry</Badge>
          <h1 className="text-4xl font-bold text-white mb-2">Request a Quote</h1>
          <p className="text-white/80 text-lg">
            Fill in your requirements and our team will get back with the best pricing
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Form */}
              <div className="lg:col-span-2 space-y-6">
                {/* Business Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center text-[#263776]">
                      <Building2 className="w-5 h-5 mr-2 text-[#F47D20]" />
                      Business Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="businessName">Business Name *</Label>
                        <Input
                          id="businessName"
                          value={formData.businessName}
                          onChange={(e) => handleChange('businessName', e.target.value)}
                          placeholder="Your company name"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="businessType">Business Type *</Label>
                        <Select value={formData.businessType} onValueChange={(v) => handleChange('businessType', v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            {businessTypes.map(type => (
                              <SelectItem key={type} value={type}>{type}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="contactPerson">Contact Person *</Label>
                        <Input
                          id="contactPerson"
                          value={formData.contactPerson}
                          onChange={(e) => handleChange('contactPerson', e.target.value)}
                          placeholder="Full name"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="gstNumber">GST Number (Optional)</Label>
                        <Input
                          id="gstNumber"
                          value={formData.gstNumber}
                          onChange={(e) => handleChange('gstNumber', e.target.value)}
                          placeholder="22AAAAA0000A1Z5"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleChange('email', e.target.value)}
                          placeholder="business@example.com"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone *</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleChange('phone', e.target.value)}
                          placeholder="+91 98765 43210"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="city">City *</Label>
                        <Input
                          id="city"
                          value={formData.city}
                          onChange={(e) => handleChange('city', e.target.value)}
                          placeholder="City"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="state">State *</Label>
                        <Input
                          id="state"
                          value={formData.state}
                          onChange={(e) => handleChange('state', e.target.value)}
                          placeholder="State"
                          required
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Product Requirements */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center text-[#263776]">
                      <Package className="w-5 h-5 mr-2 text-[#F47D20]" />
                      Product Requirements
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="productCategory">Product Category *</Label>
                        <Select value={formData.productCategory} onValueChange={(v) => handleChange('productCategory', v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            {productCategories.map(cat => (
                              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="productVariety">Specific Variety (Optional)</Label>
                        <Input
                          id="productVariety"
                          value={formData.productVariety}
                          onChange={(e) => handleChange('productVariety', e.target.value)}
                          placeholder="e.g., Alphonso, Sonaka"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="md:col-span-2">
                        <Label htmlFor="quantity">Quantity Required *</Label>
                        <Input
                          id="quantity"
                          type="number"
                          value={formData.quantity}
                          onChange={(e) => handleChange('quantity', e.target.value)}
                          placeholder="Enter quantity"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="quantityUnit">Unit</Label>
                        <Select value={formData.quantityUnit} onValueChange={(v) => handleChange('quantityUnit', v)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="kg">Kilograms (kg)</SelectItem>
                            <SelectItem value="tonnes">Tonnes</SelectItem>
                            <SelectItem value="boxes">Boxes</SelectItem>
                            <SelectItem value="crates">Crates</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="qualityGrade">Quality Grade Preference</Label>
                        <Select value={formData.qualityGrade} onValueChange={(v) => handleChange('qualityGrade', v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select grade" />
                          </SelectTrigger>
                          <SelectContent>
                            {qualityGrades.map(grade => (
                              <SelectItem key={grade} value={grade}>{grade}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="packagingPreference">Packaging Preference</Label>
                        <Select value={formData.packagingPreference} onValueChange={(v) => handleChange('packagingPreference', v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select packaging" />
                          </SelectTrigger>
                          <SelectContent>
                            {packagingOptions.map(opt => (
                              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Delivery Requirements */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center text-[#263776]">
                      <Truck className="w-5 h-5 mr-2 text-[#F47D20]" />
                      Delivery Requirements
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="deliveryFrequency">Delivery Frequency</Label>
                        <Select value={formData.deliveryFrequency} onValueChange={(v) => handleChange('deliveryFrequency', v)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select frequency" />
                          </SelectTrigger>
                          <SelectContent>
                            {deliveryFrequencies.map(freq => (
                              <SelectItem key={freq} value={freq}>{freq}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="preferredDeliveryDate">Preferred Delivery Date</Label>
                        <Input
                          id="preferredDeliveryDate"
                          type="date"
                          value={formData.preferredDeliveryDate}
                          onChange={(e) => handleChange('preferredDeliveryDate', e.target.value)}
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="additionalRequirements">Additional Requirements</Label>
                      <Textarea
                        id="additionalRequirements"
                        value={formData.additionalRequirements}
                        onChange={(e) => handleChange('additionalRequirements', e.target.value)}
                        placeholder="Any specific requirements, certifications needed, special packaging instructions, etc."
                        rows={4}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Submit Button */}
                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full bg-[#F47D20] hover:bg-[#d66b1a]"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="w-5 h-5 mr-2" />
                      Submit Enquiry
                    </>
                  )}
                </Button>
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1">
                <div className="sticky top-4 space-y-6">
                  <Card className="bg-gradient-to-br from-[#263776] to-[#1e2d5f] text-white">
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold mb-4">Why Choose ORIVANA Fresh?</h3>
                      <ul className="space-y-3">
                        {[
                          'Direct farm sourcing',
                          'Quality certified produce',
                          'Competitive wholesale pricing',
                          'Cold chain delivery',
                          'Flexible order quantities',
                          'Dedicated account manager'
                        ].map((item, idx) => (
                          <li key={idx} className="flex items-center text-sm">
                            <CheckCircle className="w-4 h-4 text-[#F47D20] mr-2 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <h3 className="font-bold text-[#263776] mb-4">Need Help?</h3>
                      <p className="text-sm text-slate-600 mb-4">
                        Our sales team is available to assist you with your requirements.
                      </p>
                      <div className="space-y-3 text-sm">
                        <div className="flex items-center">
                          <Phone className="w-4 h-4 text-[#F47D20] mr-2" />
                          <span>+91 98765 43210</span>
                        </div>
                        <div className="flex items-center">
                          <Mail className="w-4 h-4 text-[#F47D20] mr-2" />
                          <span>sales@spanxport.com</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-[#F47D20]/20">
                    <CardContent className="p-6">
                      <h3 className="font-bold text-[#263776] mb-2">Response Time</h3>
                      <p className="text-sm text-slate-600">
                        We typically respond within <span className="font-bold text-[#F47D20]">24 hours</span> with a customized quote based on your requirements.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default OrivanaEnquiry;
